import { ConflictError, UnauthorizedError } from "../errors/http_errors";
import { Badge } from "../models/badges";
import { CardTheme } from "../models/cardThemes";
import { ProfileImage } from "../models/profileImages";
import { SplashScreen } from "../models/splashScreens";

async function fetchData(input: RequestInfo, init?: RequestInit) {
  const response = await fetch(input, init);

  if (response.ok) {
    return response;
  } else {
    const errorBody = await response.json();
    const errorMessage = errorBody.error;
    if (response.status === 401) {
      throw new UnauthorizedError(errorMessage);
    } else if (response.status === 409) {
      throw new ConflictError(errorMessage);
    } else {
      throw Error(
        "Request failed with status: " +
          response.status +
          " message: " +
          errorMessage
      );
    }
  }
}

export async function fetchBadges(): Promise<Badge[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/badges/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchAllBadges(): Promise<Badge[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/data/badges/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchBadgeCount(): Promise<number> {
  const badges = await fetchBadges();
  return badges.length;
}

export async function userHasBadge(fileName: string): Promise<boolean> {
  const badges = await fetchBadges();
  return badges.some((badge) => badge.fileName === fileName);
}

export async function fetchCardThemes(): Promise<CardTheme[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/cardThemes/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchAllCardThemes(): Promise<CardTheme[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/data/cardThemes/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchProfileImages(): Promise<ProfileImage[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/profileImages/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchAllProfileImages(): Promise<ProfileImage[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/data/profileImages/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchSplashScreens(): Promise<SplashScreen[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/splashScreens/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function fetchAllSplashScreens(): Promise<SplashScreen[]> {
  const response = await fetchData(
    `${process.env.REACT_APP_API_URL}/data/splashScreens/`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  return response.json();
}

export async function createProfileImage(data: ProfileImage): Promise<void> {
  await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/profileImages/`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
}

export async function createSplashScreen(data: SplashScreen): Promise<void> {
  await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/splashScreens/`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
}

export async function createCardTheme(data: CardTheme): Promise<void> {
  await fetchData(
    `${process.env.REACT_APP_API_URL}/api/collectibles/cardThemes/`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );
}

export async function createBadge(data: Badge): Promise<void> {
  await fetchData(`${process.env.REACT_APP_API_URL}/api/collectibles/badges/`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
}
